'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { isNewSessionCode, reviewCodeKey } from '@/lib/review-i18n'
import {
  hasPendingAssistantTurn,
  REVIEW_STREAM_ENABLED,
  reviewRefetchInterval,
} from '@/lib/review-polling'
import { useTRPC } from '@/lib/trpc'
import { useUserStreamHealthy } from '@/lib/user-stream'
import { cn } from '@/lib/utils'
import { returnTargetFrom, signInUrlReturningTo } from '@/lib/return-target'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TRPCClientError } from '@trpc/client'
import {
  ChevronDown,
  Loader2,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Sparkles,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import type { ImperativePanelHandle } from 'react-resizable-panels'
import { Link } from 'react-router-dom'
import rehypeRaw from 'rehype-raw'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { useMediaQuery } from 'usehooks-ts'

interface DemoSectionProps {
  onReady?: () => void
  initialGameId?: string
  titleKey?: string
  subtitle1Key?: string
  subtitle2Key?: string | null
}

export function DemoSection({
  onReady,
  initialGameId,
  titleKey = 'nav.review',
  subtitle1Key = 'demo.subtitle1',
  subtitle2Key = 'demo.subtitle2',
}: DemoSectionProps = {}) {
  const [gameId, setGameId] = useState(initialGameId ?? '')
  const [gameIdError, setGameIdError] = useState('')
  const [preloadError, setPreloadError] = useState('')
  const [isLoadingGame, setIsLoadingGame] = useState(false)
  const [preloadedGame, setPreloadedGame] = useState<{
    gameId: string
    godLog: string
    language: string
  } | null>(null)
  const [isPreloading, setIsPreloading] = useState(!!initialGameId)
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null)
  const [draft, setDraft] = useState('')
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null)
  const [optimistic, setOptimistic] = useState<{
    content: string
    assistantMessageId: string | null
  } | null>(null)
  const [sendErrorCode, setSendErrorCode] = useState<string | null>(null)
  const [sessionsCollapsed, setSessionsCollapsed] = useState(false)
  const [mobileTab, setMobileTab] = useState('prompt')
  const [pickerExpanded, setPickerExpanded] = useState(true)
  const sessionsPanelRef = useRef<ImperativePanelHandle>(null)
  const chatScrollRef = useRef<HTMLDivElement>(null)
  const autoSelectedGameRef = useRef<string | null>(null)
  const { t, language } = useI18n()
  const languageKey = language as 'en' | 'zh' | 'fr' | 'ar' | 'es' | 'ja' | 'tw' | 'ko'
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuth()
  const isDesktop = useMediaQuery('(min-width: 1024px)')

  const preloadDemoGameQuery = useQuery({
    ...trpc.queryRouter.preloadDemoGame.queryOptions({
      language: languageKey,
    }),
    enabled: !initialGameId,
  })

  const loadedGameId = preloadedGame?.gameId ?? null

  const seatsQuery = useQuery({
    ...trpc.reviewRouter.getGameSeats.queryOptions({
      gameId: loadedGameId ?? '',
    }),
    enabled: !!loadedGameId,
  })

  const sessionsQuery = useQuery({
    ...trpc.reviewRouter.listSessions.queryOptions(),
    enabled: isAuthenticated,
  })

  const activeSession = activeSessionId
    ? sessionsQuery.data?.find(session => session.id === activeSessionId)
    ?? null
    : null

  const promptSeat = activeSession
    ? { gameId: activeSession.gameId, position: activeSession.position }
    : loadedGameId && selectedPosition !== null
      ? { gameId: loadedGameId, position: selectedPosition }
      : null

  const userStreamHealthy = useUserStreamHealthy()
  const streamHealthy = REVIEW_STREAM_ENABLED && userStreamHealthy

  const messagesQuery = useQuery({
    ...trpc.reviewRouter.getMessages.queryOptions({
      sessionId: activeSessionId ?? '',
    }),
    enabled: isAuthenticated && !!activeSessionId,
    refetchOnWindowFocus: false,
    refetchInterval: query => reviewRefetchInterval(query, streamHealthy),
    refetchIntervalInBackground: true,
  })

  const hasPending = hasPendingAssistantTurn(messagesQuery.data)

  const createSessionMutation = useMutation(
    trpc.reviewRouter.createSession.mutationOptions(),
  )
  const sendMessageMutation = useMutation(
    trpc.reviewRouter.sendMessage.mutationOptions(),
  )
  const isSending
    = createSessionMutation.isPending || sendMessageMutation.isPending

  useEffect(() => {
    if (initialGameId !== undefined)
      setGameId(initialGameId)
  }, [initialGameId])

  useEffect(() => {
    setSelectedPosition(null)
    setActiveSessionId(null)
    setOptimistic(null)
    setSendErrorCode(null)
  }, [loadedGameId])

  useEffect(() => {
    if (!loadedGameId)
      setPickerExpanded(true)
    else if (!isDesktop)
      setPickerExpanded(false)
  }, [loadedGameId, isDesktop])

  useEffect(() => {
    if (!loadedGameId || activeSessionId)
      return
    const seats = seatsQuery.data
    if (!seats || autoSelectedGameRef.current === loadedGameId)
      return
    autoSelectedGameRef.current = loadedGameId
    const first = seats.find(seat => seat.chattable)
    if (first)
      setSelectedPosition(first.position)
  }, [loadedGameId, seatsQuery.data, activeSessionId])

  useEffect(() => {
    if (
      optimistic?.assistantMessageId
      && messagesQuery.data?.some(m => m.id === optimistic.assistantMessageId)
    ) {
      setOptimistic(null)
    }
  }, [optimistic, messagesQuery.data])

  useEffect(() => {
    const el = chatScrollRef.current
    if (el)
      el.scrollTop = el.scrollHeight
  }, [messagesQuery.data, optimistic])

  useEffect(() => {
    if (!initialGameId?.trim())
      return

    let cancelled = false
    const load = async () => {
      setIsPreloading(true)
      setGameIdError('')
      try {
        const result = await queryClient.fetchQuery(
          trpc.queryRouter.searchDemoGame.queryOptions({
            language: languageKey,
            gameId: initialGameId.trim(),
          }),
        )
        if (!cancelled && result) {
          setPreloadedGame(result)
        }
      }
      catch (error) {
        if (!cancelled) {
          console.error('Failed to load demo game by ID:', error)
          setGameIdError(t('demo.gameIdNotFound'))
        }
      }
      finally {
        if (!cancelled) {
          setIsPreloading(false)
          onReady?.()
        }
      }
    }
    load()
    return () => { cancelled = true }
  }, [initialGameId, languageKey, queryClient, trpc.queryRouter.searchDemoGame, t, onReady])

  useEffect(() => {
    if (initialGameId)
      return
    if (preloadDemoGameQuery.data) {
      const result = preloadDemoGameQuery.data
      setPreloadedGame(result)
      setPreloadError('')
      setIsPreloading(false)
      onReady?.()
    }

    if (preloadDemoGameQuery.error) {
      console.error('Failed to preload demo game:', preloadDemoGameQuery.error)
      setPreloadError(t('demo.errorGeneric'))
      setIsPreloading(false)
      onReady?.()
    }
  }, [initialGameId, preloadDemoGameQuery.data, preloadDemoGameQuery.error, t, onReady])

  const handleChangeGame = () => {
    setGameId('')
    setGameIdError('')
    setPreloadError('')
    preloadDemoGameQuery.refetch()
  }

  const handleSearchGame = async () => {
    setGameIdError('')
    setPreloadError('')

    const trimmedGameId = gameId.trim()
    if (!trimmedGameId)
      return

    setIsLoadingGame(true)
    setIsPreloading(true)

    try {
      const result = await queryClient.fetchQuery(
        trpc.queryRouter.searchDemoGame.queryOptions({
          language: languageKey,
          gameId: trimmedGameId,
        }),
      )

      if (result) {
        setPreloadedGame(result)
        setIsPreloading(false)
      }
    }
    catch (error) {
      console.error('Failed to search game:', error)
      const errorMessage = error instanceof Error ? error.message : t('demo.errorGeneric')

      if (errorMessage.includes('not found')) {
        setGameIdError(t('demo.gameIdNotFound'))
      }
      else {
        setGameIdError(t('demo.gameLoadError'))
      }

      setIsPreloading(false)
    }
    finally {
      setIsLoadingGame(false)
    }
  }

  const handleSelectSession = (sessionId: string) => {
    setActiveSessionId(sessionId)
    setOptimistic(null)
    setSendErrorCode(null)
    setMobileTab('chat')
  }

  const extractReviewCode = (error: unknown): string =>
    error instanceof TRPCClientError && error.message
      ? error.message
      : 'generic'

  const canConverse
    = !!activeSessionId || (!!loadedGameId && selectedPosition !== null)
  const composerDisabled = !canConverse || hasPending || isSending

  const handleSend = async () => {
    const content = draft.trim()
    if (!content || content.length > 4000 || composerDisabled)
      return
    const seat = promptSeat
    if (!activeSessionId && !seat)
      return
    setSendErrorCode(null)
    setOptimistic({ content, assistantMessageId: null })
    setDraft('')
    try {
      let sessionId = activeSessionId
      if (!sessionId) {
        if (!seat) {
          setOptimistic(null)
          return
        }
        const session = await createSessionMutation.mutateAsync(seat)
        sessionId = session.id
        setActiveSessionId(session.id)
        queryClient.invalidateQueries({
          queryKey: trpc.reviewRouter.listSessions.queryKey(),
        })
      }
      const { assistantMessageId } = await sendMessageMutation.mutateAsync({
        sessionId,
        content,
      })
      setOptimistic({ content, assistantMessageId })
      queryClient.invalidateQueries({
        queryKey: trpc.reviewRouter.getMessages.queryKey({ sessionId }),
      })
    }
    catch (error) {
      console.error('Failed to send review message:', error)
      setOptimistic(null)
      setDraft(content)
      setSendErrorCode(extractReviewCode(error))
    }
  }

  const handleNewSession = async () => {
    if (!promptSeat || createSessionMutation.isPending)
      return
    try {
      const session = await createSessionMutation.mutateAsync(promptSeat)
      setActiveSessionId(session.id)
      setOptimistic(null)
      setSendErrorCode(null)
      queryClient.invalidateQueries({
        queryKey: trpc.reviewRouter.listSessions.queryKey(),
      })
    }
    catch (error) {
      console.error('Failed to create review session:', error)
      setSendErrorCode(extractReviewCode(error))
    }
  }

  const renderSessionsBody = () => {
    if (!isAuthenticated) {
      return (
        <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
          {t('review.sessions.signin')}
        </div>
      )
    }
    if (sessionsQuery.isLoading) {
      return (
        <div className="flex h-full items-center justify-center text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )
    }
    if (sessionsQuery.isError) {
      return (
        <div className="flex h-full items-center justify-center p-6 text-center text-sm text-red-600 dark:text-red-400">
          {t('review.error.generic')}
        </div>
      )
    }
    const sessions = sessionsQuery.data ?? []
    if (sessions.length === 0) {
      return (
        <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
          {t('review.sessions.empty')}
        </div>
      )
    }
    return (
      <ScrollArea className="h-full">
        <div className="space-y-1 p-2">
          {sessions.map(session => (
            <button
              key={session.id}
              type="button"
              onClick={() => handleSelectSession(session.id)}
              aria-current={session.id === activeSessionId ? 'true' : undefined}
              className={cn(
                'w-full truncate rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                session.id === activeSessionId
                  ? 'bg-accent font-medium text-accent-foreground'
                  : 'text-muted-foreground',
              )}
            >
              { }
              {session.title}
            </button>
          ))}
        </div>
      </ScrollArea>
    )
  }

  const sessionsColumn = (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-sm font-medium">{t('review.sessions.title')}</h3>
        {isDesktop && (
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            aria-label={t('review.sessions.title')}
            aria-expanded
            onClick={() => sessionsPanelRef.current?.collapse()}
          >
            <PanelLeftClose className="size-4" />
          </Button>
        )}
      </div>
      <div className="min-h-0 flex-1">{renderSessionsBody()}</div>
    </div>
  )

  const renderPromptBody = () => {
    if (isPreloading) {
      return (
        <div className="flex h-full items-center justify-center text-muted-foreground">
          <div className="text-center">
            <Loader2 className="mx-auto mb-4 size-12 animate-spin" />
            <p className="text-lg">{t('demo.loadingGame')}</p>
            <p className="mt-2 text-sm">{t('demo.preloadingGame')}</p>
          </div>
        </div>
      )
    }
    if (preloadedGame?.godLog) {
      return (
        <ScrollArea className="h-full">
          {
          }
          <div className="prose prose-sm max-w-none break-words p-4 leading-relaxed text-foreground dark:prose-invert [&_pre]:overflow-x-auto [&_table]:block [&_table]:overflow-x-auto">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              rehypePlugins={[rehypeRaw]}
            >
              {preloadedGame.godLog}
            </ReactMarkdown>
          </div>
        </ScrollArea>
      )
    }
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
        {t('review.prompt.empty')}
      </div>
    )
  }

  const promptColumn = (
    <div className="flex h-full flex-col">
      {
      }
      <div className="hidden items-center gap-2 border-b px-4 py-3 lg:flex">
        {isDesktop && sessionsCollapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            aria-label={t('review.sessions.title')}
            aria-expanded={false}
            onClick={() => sessionsPanelRef.current?.expand()}
          >
            <PanelLeftOpen className="size-4" />
          </Button>
        )}
        <h3 className="text-sm font-medium">{t('review.prompt.title')}</h3>
      </div>
      <div className="min-h-0 flex-1">{renderPromptBody()}</div>
    </div>
  )

  type ReviewMessageRow = NonNullable<typeof messagesQuery.data>[number]

  const renderMessage = (message: ReviewMessageRow) => {
    if (message.role === 'user') {
      return (
        <div key={message.id} className="flex justify-end">
          <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">
            {message.content}
          </div>
        </div>
      )
    }
    if (message.status === 'error') {
      return (
        <div key={message.id} className="flex justify-start">
          <div className="max-w-[85%] rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-red-600 dark:text-red-400">
            {t('review.error.generationFailed')}
          </div>
        </div>
      )
    }
    if (message.status === 'pending') {
      return (
        <div
          key={message.id}
          className="flex justify-start"
          data-testid="review-pending-indicator"
        >
          <div className="rounded-lg border bg-background px-3 py-2">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        </div>
      )
    }
    return (
      <div key={message.id} className="flex justify-start">
        <div className="max-w-[85%] rounded-lg border bg-background px-3 py-2">
          <div className="prose prose-sm max-w-none text-foreground dark:prose-invert">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              rehypePlugins={[rehypeRaw]}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    )
  }

  const showOptimistic = !!optimistic && (
    !optimistic.assistantMessageId
    || !messagesQuery.data?.some(m => m.id === optimistic.assistantMessageId)
  )

  const renderChatBody = () => {
    if (activeSessionId && messagesQuery.isLoading) {
      return (
        <div className="flex h-full items-center justify-center text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )
    }
    if (activeSessionId && messagesQuery.isError) {
      return (
        <div className="flex h-full items-center justify-center p-6 text-center text-sm text-red-600 dark:text-red-400">
          {t('review.error.generic')}
        </div>
      )
    }
    const messages = (activeSessionId ? messagesQuery.data : undefined) ?? []
    if (messages.length === 0 && !showOptimistic) {
      return (
        <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
          {t('review.chat.empty')}
        </div>
      )
    }
    return (
      <div className="space-y-3 p-4">
        {messages.map(message => renderMessage(message))}
        {showOptimistic && optimistic && (
          <div className="flex justify-end">
            <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">
              {optimistic.content}
            </div>
          </div>
        )}
      </div>
    )
  }

  const chatColumn = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <h3 className="text-sm font-medium">{t('review.tabs.chat')}</h3>
        {activeSession && (
          <span className="truncate text-xs text-muted-foreground">
            {activeSession.title}
          </span>
        )}
      </div>
      <div ref={chatScrollRef} className="min-h-0 flex-1 overflow-y-auto">
        {renderChatBody()}
      </div>
      {sendErrorCode && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
          <p className="text-sm text-red-600 dark:text-red-400">
            {t(reviewCodeKey(sendErrorCode))}
          </p>
          {isNewSessionCode(sendErrorCode) && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleNewSession}
              disabled={createSessionMutation.isPending}
            >
              {t('review.newSession')}
            </Button>
          )}
        </div>
      )}
      {isAuthenticated ? (
        <div className="border-t p-3">
          {(hasPending || isSending) && (
            <p className="mb-2 text-xs text-muted-foreground">
              {t('review.chat.pendingHint')}
            </p>
          )}
          <div className="flex items-end gap-2">
            <Textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === 'Enter'
                  && !e.shiftKey
                  && !e.nativeEvent.isComposing
                ) {
                  e.preventDefault()
                  handleSend()
                }
              }}
              maxLength={4000}
              placeholder={t('review.chat.placeholder')}
              aria-label={t('review.chat.placeholder')}
              className="max-h-40 min-h-10 flex-1 resize-none"
              disabled={composerDisabled}
            />
            <Button
              onClick={handleSend}
              disabled={composerDisabled || !draft.trim()}
            >
              {t('review.chat.send')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-center border-t p-4">
          <Button asChild>
            <Link
              to={signInUrlReturningTo(returnTargetFrom(window.location))}
            >
              {t('review.chat.signinCta')}
            </Link>
          </Button>
        </div>
      )}
    </div>
  )

  const selectedSeat = (seatsQuery.data ?? []).find(
    seat => seat.position === selectedPosition,
  )

  return (
    <div className="min-h-dvh bg-section-dark px-4 py-8 sm:px-6 sm:py-20 lg:px-8">
      <div className="w-full">
        { }
        <div className="mb-6 text-center sm:mb-12">
          <h2 className="mb-2 text-xl font-bold text-foreground sm:mb-4 sm:text-2xl md:text-3xl lg:text-4xl">
            {t(titleKey)}
          </h2>
          <p className="mx-auto hidden max-w-4xl text-lg text-muted-foreground sm:block">
            {t(subtitle1Key)}
          </p>
          {subtitle2Key && (
            <p className="mx-auto hidden max-w-4xl text-sm text-muted-foreground/80 sm:block">
              {t(subtitle2Key)}
            </p>
          )}
        </div>

        <div className="w-full">
          <Card>
            <CardContent className="space-y-6 pt-6">
              {
              }
              {!isDesktop && loadedGameId && !pickerExpanded && (
                <div className="mx-auto max-w-4xl">
                  <button
                    type="button"
                    onClick={() => setPickerExpanded(true)}
                    aria-label={t('review.picker.expand')}
                    className="flex w-full items-center justify-between gap-2 rounded-lg border bg-background px-4 py-3 text-left text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate">
                      <span className="text-muted-foreground">{t('demo.gameId')}: </span>
                      <span className="font-medium">{loadedGameId}</span>
                    </span>
                    {selectedSeat && (
                      <span className="shrink-0 text-muted-foreground">
                        {`#${selectedSeat.position} ${selectedSeat.name}`}
                      </span>
                    )}
                    <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                  </button>
                </div>
              )}
              <div
                className={cn(
                  'mx-auto max-w-4xl space-y-4',
                  !isDesktop && loadedGameId && !pickerExpanded && 'hidden',
                )}
              >
                {
                }
                <div className="flex flex-col gap-4 sm:flex-row">
                  { }
                  <div className="min-w-0 flex-1 space-y-2">
                    <Label htmlFor="gameId">{t('demo.gameId')}</Label>
                    <Input
                      id="gameId"
                      name="gameId"
                      type="text"
                      placeholder={t('demo.gameIdPlaceholder')}
                      value={gameId}
                      onChange={(e) => {
                        setGameId(e.target.value)
                        setGameIdError('')
                      }}
                      disabled={isLoadingGame || isPreloading}
                      className={
                        gameIdError ? 'border-red-500 focus:ring-red-500' : ''
                      }
                    />
                    {gameIdError && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {gameIdError}
                      </p>
                    )}
                  </div>

                  {
                  }
                  {(loadedGameId || !gameIdError) && (
                    <div className="min-w-0 flex-1 space-y-2">
                      <Label htmlFor="reviewSeat">{t('review.seat.placeholder')}</Label>
                      {seatsQuery.isError ? (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {t('review.error.generic')}
                        </p>
                      ) : (
                        <Select
                          value={selectedPosition !== null ? String(selectedPosition) : ''}
                          onValueChange={(value) => {
                            setSelectedPosition(Number(value))
                            setActiveSessionId(null)
                            setOptimistic(null)
                            setSendErrorCode(null)
                          }}
                          disabled={!loadedGameId || isPreloading || seatsQuery.isLoading}
                        >
                          <SelectTrigger id="reviewSeat" className="w-full">
                            <SelectValue placeholder={t('review.seat.placeholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {(seatsQuery.data ?? []).map(seat => (
                              <SelectItem
                                key={seat.position}
                                value={String(seat.position)}
                                disabled={!seat.chattable}
                              >
                                {`#${seat.position} · ${seat.name} · ${seat.modelName}`}
                                {!seat.chattable && seat.reason
                                  && ` — ${t(reviewCodeKey(seat.reason))}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>

                {
                }
                <div className="flex flex-wrap justify-center gap-2 pt-4">
                  <Button
                    onClick={handleSearchGame}
                    disabled={isLoadingGame || isPreloading || !gameId.trim()}
                    size="lg"
                    className="min-w-[200px]"
                  >
                    <div className="flex items-center">
                      {isLoadingGame ? (
                        <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          <span>{t('demo.loading')}</span>
                        </>
                      ) : (
                        <>
                          <Search className="mr-2 size-4" />
                          <span>{t('demo.searchGame')}</span>
                        </>
                      )}
                    </div>
                  </Button>
                  <Button
                    onClick={handleChangeGame}
                    disabled={isPreloading}
                    variant="outline"
                    size="lg"
                  >
                    <div className="flex items-center">
                      {isPreloading ? (
                        <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          <span>{t('demo.loading')}</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 size-4" />
                          <span>{t('demo.changeGame')}</span>
                        </>
                      )}
                    </div>
                  </Button>
                </div>
                {preloadError && (
                  <p className="text-center text-sm text-red-600 dark:text-red-400">
                    {preloadError}
                  </p>
                )}
              </div>

              {
              }
              <div className="h-[80dvh] max-h-[1100px] overflow-hidden rounded-lg border bg-muted/50">
                {isDesktop ? (
                  <ResizablePanelGroup direction="horizontal">
                    <ResizablePanel
                      ref={sessionsPanelRef}
                      defaultSize={20}
                      minSize={12}
                      collapsible
                      onCollapse={() => setSessionsCollapsed(true)}
                      onExpand={() => setSessionsCollapsed(false)}
                    >
                      {sessionsColumn}
                    </ResizablePanel>
                    <ResizableHandle withHandle />
                    <ResizablePanel defaultSize={40} minSize={20}>
                      {promptColumn}
                    </ResizablePanel>
                    <ResizableHandle withHandle />
                    <ResizablePanel defaultSize={40} minSize={20}>
                      {chatColumn}
                    </ResizablePanel>
                  </ResizablePanelGroup>
                ) : (
                  <Tabs
                    value={mobileTab}
                    onValueChange={setMobileTab}
                    className="flex h-full flex-col"
                  >
                    <TabsList className="mx-3 mt-3 grid grid-cols-3">
                      <TabsTrigger value="sessions">{t('review.tabs.sessions')}</TabsTrigger>
                      <TabsTrigger value="prompt">{t('review.tabs.prompt')}</TabsTrigger>
                      <TabsTrigger value="chat">{t('review.tabs.chat')}</TabsTrigger>
                    </TabsList>
                    <TabsContent value="sessions" className="min-h-0 flex-1">
                      {sessionsColumn}
                    </TabsContent>
                    <TabsContent value="prompt" className="min-h-0 flex-1">
                      {promptColumn}
                    </TabsContent>
                    <TabsContent value="chat" className="min-h-0 flex-1">
                      {chatColumn}
                    </TabsContent>
                  </Tabs>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
